import { c as _c } from "react/compiler-runtime";
import { useQuery, type UseQueryResult, type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import type * as fetchClient from 'shared/api/fetch';
import * as analytics from 'shared/api/analytics';
import useMutationAnalytics from 'shared/hooks/useMutationAnalytics';
import toCamelCase from 'camelcase-keys';
import { getRecommendationsRules, getRecommendationsRulesStatuses, createRecommendationRules, type RecommendationRuleConfigRequest, type RecommendationsRulesMetadata, type RecommendationRuleConfigResponse, type RecommendationRuleStatusResponse, updateRecommendationRules, type RecommendationRuleStatusRequest, createRecommendationsRulesStatuses, updateRecommendationsRulesStatuses, deleteRecommendationsRulesStatuses, deleteRecommendationRules } from 'shared/api/recommendations';
import { type RecommendationRulesMetadataJson, getRecommendationsRulesMetadataJson, getRecommendationHowToFix, type RecommendationMetadataJson, type RecommendationMetadata } from 'shared/assetsBucket/recommendations';
import { objectEntries, objectKeys } from 'shared/utils';
import { formatDateToString } from 'shared/dateFns';
import { convertSnakeCaseToCamelCase } from 'shared/utils/stringFormatter';
import { EXCLUDED_HC_PLATFORMS, HEALTH_CHECKERS_V2_SOURCE_SERVICE } from '../constants';
import { type ExtendedPlatforms, type Platforms } from 'shared/api/accounts';
import { uniq } from 'ramda';
import { getPlatformDetailsByPlatform } from 'src/dataSource/utils';
const queryKeys = {
  recommendationsRulesMetadata: 'recommendations-rules-metadata',
  recommendationsRuleHowToFix: 'recommendations-rule-how-to-fix',
  recommendationsRules: 'recommendations-rules',
  recommendationsRulesStatuses: 'recommendations-rules-statuses',
  recommendationsSupportedPlatforms: 'recommendations-supported-platforms'
};
const getRecommendationsRulesQueryKey = (clientId: number) => [queryKeys.recommendationsRules, clientId] as const;
const getRecommendationsRulesStatusesQueryKey = (clientId: number) => [queryKeys.recommendationsRulesStatuses, clientId] as const;
export const getActivityFeedFilters = (filters?: analytics.Filter[]): analytics.Filter[] => [...(filters ?? []), {
  field: 'source_service',
  operator: '=',
  value: HEALTH_CHECKERS_V2_SOURCE_SERVICE
}, {
  field: 'platform',
  operator: 'NOT_IN',
  value: EXCLUDED_HC_PLATFORMS
}];
export const useMutationActivityFeed = (clientId: number): UseMutationResult<analytics.FeedResp[], fetchClient.ClientError, analytics.AnalyticsReq> => {
  return useMutationAnalytics(async (variables: analytics.AnalyticsReq) => {
    const feedData = await analytics.postFeed({
      ...variables,
      collection: 'event_feed',
      report: 'event_feed',
      clientId,
      filters: getActivityFeedFilters(variables?.filters)
    });

    // This is needed for now, as the very first events did not have
    // the totalNumEntities and totalNumEntitiesWithIssues fields,
    // as they were added via a Fix after the health checkers have been running
    // for a few days. So we need to set them to the length of the data array.
    feedData.forEach(event => {
      event.jsonPayload.totalNumEntities ??= event.jsonPayload.data.length;
      event.jsonPayload.totalNumEntitiesWithIssues ??= event.jsonPayload.data.length;
    });
    return feedData;
  });
};
const getHCPlatformFromMetadataJson = (metadataJson: RecommendationMetadataJson): ExtendedPlatforms => Object.keys(metadataJson.platform)[0] as ExtendedPlatforms;
const getPlatformFromMetadataJson = (metadataJson: RecommendationMetadataJson): Platforms => getPlatformDetailsByPlatform(getHCPlatformFromMetadataJson(metadataJson)).platform;
const isOnExcludedPlatform = (metadataJson: RecommendationMetadataJson): boolean => EXCLUDED_HC_PLATFORMS.includes(getHCPlatformFromMetadataJson(metadataJson));
export const createRecommendationRuleMetadataFromJson = (data: RecommendationRulesMetadataJson): RecommendationsRulesMetadata => Object.fromEntries(objectEntries(data).filter(([_, metadataJson]) => !isOnExcludedPlatform(metadataJson)).map(([key, metadataJson]) => {
  const metadata: RecommendationMetadata = {
    ...toCamelCase({
      ...metadataJson
    }),
    platform: getPlatformFromMetadataJson(metadataJson),
    stage: metadataJson.stage ?? 'active'
  };
  const newDefaultColumns = metadata.defaultColumns.map((column: string) => convertSnakeCaseToCamelCase(column));
  metadata.outputColumns = toCamelCase({
    ...metadata.outputColumns
  });
  if (metadata.params != null) {
    metadata.params = toCamelCase({
      ...metadata.params
    });
  }
  for (const column of objectKeys(metadata.outputColumns)) {
    if (!newDefaultColumns.includes(column)) {
      const index = newDefaultColumns.indexOf(column.toLowerCase());
      if (index >= 0) {
        newDefaultColumns[index] = column;
      }
    }
  }
  metadata.defaultColumns = newDefaultColumns;
  return [key, metadata];
}));
export const useQueryRecommendationsRulesMetadata = t0 => {
  const $ = _c(3);
  const {
    enabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [queryKeys.recommendationsRulesMetadata];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== enabled) {
    t2 = {
      queryKey: t1,
      queryFn: _temp,
      enabled,
      select: _temp2
    };
    $[1] = enabled;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
};
export const useQueryRecommendationsSupportedPlatforms = t0 => {
  const $ = _c(3);
  const {
    enabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [queryKeys.recommendationsSupportedPlatforms];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== enabled) {
    t2 = {
      queryKey: t1,
      queryFn: _temp3,
      enabled,
      select: _temp6
    };
    $[1] = enabled;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
};
export const useQueryRuleHowToFix = t0 => {
  const $ = _c(7);
  const {
    ruleId,
    enabled
  } = t0;
  let t1;
  let t2;
  if ($[0] !== ruleId) {
    t1 = [queryKeys.recommendationsRuleHowToFix, ruleId];
    t2 = async () => await getRecommendationHowToFix(ruleId);
    $[0] = ruleId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== enabled || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled
    };
    $[3] = enabled;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};
export const useQueryRecommendationsRulesConfigs = t0 => {
  const $ = _c(8);
  const {
    clientId,
    enabled
  } = t0;
  let t1;
  if ($[0] !== clientId) {
    t1 = getRecommendationsRulesQueryKey(clientId);
    $[0] = clientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== clientId) {
    t2 = async () => await getRecommendationsRules(clientId);
    $[2] = clientId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== enabled || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled
    };
    $[4] = enabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useQueryRecommendationsRulesStatuses = t0 => {
  const $ = _c(8);
  const {
    clientId,
    enabled
  } = t0;
  let t1;
  if ($[0] !== clientId) {
    t1 = getRecommendationsRulesStatusesQueryKey(clientId);
    $[0] = clientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== clientId) {
    t2 = async () => await getRecommendationsRulesStatuses(clientId);
    $[2] = clientId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== enabled || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled
    };
    $[4] = enabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
interface MutationBaseParams {
  onSuccess?: () => void;
  onError?: () => void;
  onSettled?: () => void;
}
interface MutationCreateRecommendationRulesConfigsParams extends MutationBaseParams {
  data: RecommendationRuleConfigRequest;
}
export const useMutationCreateRecommendationRulesConfigs = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp7,
      onMutate: async t1 => {
        const {
          data: data_0
        } = t1;
        const rulesQueryKey = getRecommendationsRulesQueryKey(data_0.clientId);
        await queryClient.cancelQueries({
          queryKey: rulesQueryKey
        });
        const previousRules = queryClient.getQueryData(rulesQueryKey);
        if (previousRules != null) {
          queryClient.setQueryData(rulesQueryKey, old => {
            const newRecommendation = {
              id: 0,
              createdBy: 0,
              createdAt: formatDateToString({
                date: new Date(),
                targetFormat: "yyyy-MM-dd HH:mm:ss"
              }),
              updatedBy: 0,
              updatedAt: formatDateToString({
                date: new Date(),
                targetFormat: "yyyy-MM-dd HH:mm:ss"
              }),
              status: "active",
              ...data_0
            };
            if (old == null) {
              return [newRecommendation];
            }
            return [...old, newRecommendation];
          });
        }
        return {
          previousRules,
          rulesQueryKey
        };
      },
      onError: (_, t2, context) => {
        const {
          onError
        } = t2;
        if (context?.previousRules != null && context.rulesQueryKey != null) {
          queryClient.setQueryData(context.rulesQueryKey, context.previousRules);
        }
        onError?.();
      },
      onSuccess: (__0, t3) => {
        const {
          data: data_1,
          onSuccess
        } = t3;
        queryClient.invalidateQueries({
          queryKey: getRecommendationsRulesQueryKey(data_1.clientId)
        });
        onSuccess?.();
      },
      onSettled: _temp8
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
interface MutationUpdateRecommendationRulesConfigsParams extends MutationBaseParams {
  data: RecommendationRuleConfigRequest;
  configId: number;
  clientId: number;
}
export const useMutationUpdateRecommendationRulesConfigs = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp9,
      onMutate: async t1 => {
        const {
          data: data_0,
          configId: configId_0,
          clientId: clientId_0
        } = t1;
        const rulesQueryKey = getRecommendationsRulesQueryKey(clientId_0);
        await queryClient.cancelQueries({
          queryKey: rulesQueryKey
        });
        const previousRules = queryClient.getQueryData(rulesQueryKey);
        const singleRuleKey = [queryKeys.recommendationsRules, configId_0, clientId_0];
        const previousSingleRule = queryClient.getQueryData(singleRuleKey);
        if (previousRules != null) {
          queryClient.setQueryData(rulesQueryKey, old => {
            if (old == null) {
              return [];
            }
            if (data_0 == null) {
              return old.filter(t2 => {
                const {
                  id
                } = t2;
                return id !== configId_0;
              });
            }
            return old.map(rule => {
              if (rule.id === configId_0) {
                return {
                  id: rule.id,
                  createdBy: rule.createdBy,
                  createdAt: rule.createdAt,
                  updatedBy: rule.updatedBy,
                  updatedAt: rule.updatedAt,
                  status: rule.status,
                  ...data_0
                };
              }
              return rule;
            });
          });
        }
        if (previousSingleRule != null) {
          queryClient.setQueryData(singleRuleKey, {
            id: previousSingleRule.id,
            createdBy: previousSingleRule.createdBy,
            createdAt: previousSingleRule.createdAt,
            updatedBy: previousSingleRule.updatedBy,
            updatedAt: previousSingleRule.updatedAt,
            status: previousSingleRule.status,
            ...data_0
          });
        }
        return {
          previousRules,
          previousSingleRule,
          singleRuleKey,
          rulesQueryKey
        };
      },
      onError: (_, t3, context) => {
        const {
          onError
        } = t3;
        if (context?.previousRules != null && context.rulesQueryKey != null) {
          queryClient.setQueryData(context.rulesQueryKey, context.previousRules);
        }
        if (context?.previousSingleRule != null) {
          queryClient.setQueryData(context.singleRuleKey, context.previousSingleRule);
        }
        onError?.();
      },
      onSuccess: (__0, t4, context_0) => {
        const {
          onSuccess,
          clientId: clientId_1
        } = t4;
        queryClient.invalidateQueries({
          queryKey: getRecommendationsRulesQueryKey(clientId_1)
        });
        if (context_0?.singleRuleKey != null) {
          queryClient.invalidateQueries({
            queryKey: context_0.singleRuleKey
          });
        }
        onSuccess?.();
      },
      onSettled: (__1, __, t5) => {
        const {
          onSettled,
          clientId: clientId_2
        } = t5;
        queryClient.invalidateQueries({
          queryKey: getRecommendationsRulesQueryKey(clientId_2)
        });
        onSettled?.();
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
interface MutationDeleteRecommendationRulesConfigsParams extends MutationBaseParams {
  configId: number;
  clientId: number;
}
export const useMutationDeleteRecommendationRulesConfigs = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp10,
      onMutate: async t1 => {
        const {
          configId: configId_0,
          clientId: clientId_0
        } = t1;
        const rulesQueryKey = getRecommendationsRulesQueryKey(clientId_0);
        await queryClient.cancelQueries({
          queryKey: rulesQueryKey
        });
        const previousRules = queryClient.getQueryData(rulesQueryKey);
        const singleRuleKey = [queryKeys.recommendationsRules, configId_0, clientId_0];
        const previousSingleRule = queryClient.getQueryData(singleRuleKey);
        if (previousRules != null) {
          queryClient.setQueryData(rulesQueryKey, old => {
            if (old == null) {
              return [];
            }
            return old.filter(t2 => {
              const {
                id
              } = t2;
              return id !== configId_0;
            });
          });
        }
        if (previousSingleRule != null) {
          queryClient.removeQueries({
            queryKey: singleRuleKey
          });
        }
        return {
          previousRules,
          previousSingleRule,
          singleRuleKey,
          rulesQueryKey
        };
      },
      onError: (_, t3, context) => {
        const {
          onError
        } = t3;
        if (context?.previousRules != null && context.rulesQueryKey != null) {
          queryClient.setQueryData(context.rulesQueryKey, context.previousRules);
        }
        if (context?.previousSingleRule != null) {
          queryClient.setQueryData(context.singleRuleKey, context.previousSingleRule);
        }
        onError?.();
      },
      onSuccess: (__0, t4, context_0) => {
        const {
          onSuccess
        } = t4;
        if (context_0?.singleRuleKey != null) {
          queryClient.invalidateQueries({
            queryKey: context_0.singleRuleKey
          });
        }
        onSuccess?.();
      },
      onSettled: (__1, __, t5) => {
        const {
          onSettled,
          clientId: clientId_1
        } = t5;
        queryClient.invalidateQueries({
          queryKey: getRecommendationsRulesQueryKey(clientId_1)
        });
        onSettled?.();
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
interface MutationCreateRecommendationsRulesStatusesParams extends MutationBaseParams {
  data: RecommendationRuleStatusRequest;
}
export const useMutationCreateRecommendationsRulesStatuses = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp11,
      onMutate: async t1 => {
        const {
          data: data_0
        } = t1;
        const statusesQueryKey = getRecommendationsRulesStatusesQueryKey(data_0.clientId);
        await queryClient.cancelQueries({
          queryKey: statusesQueryKey
        });
        const previousStatuses = queryClient.getQueryData(statusesQueryKey);
        if (previousStatuses != null) {
          queryClient.setQueryData(statusesQueryKey, old => {
            const newRecommendation = {
              id: 0,
              createdBy: 0,
              createdAt: formatDateToString({
                date: new Date(),
                targetFormat: "yyyy-MM-dd HH:mm:ss"
              }),
              updatedBy: 0,
              updatedAt: formatDateToString({
                date: new Date(),
                targetFormat: "yyyy-MM-dd HH:mm:ss"
              }),
              status: "active",
              ...data_0
            };
            if (old == null) {
              return [newRecommendation];
            }
            return [...old, newRecommendation];
          });
        }
        return {
          previousStatuses,
          statusesQueryKey
        };
      },
      onError: (_, t2, context) => {
        const {
          onError
        } = t2;
        if (context?.previousStatuses != null && context.statusesQueryKey != null) {
          queryClient.setQueryData(context.statusesQueryKey, context.previousStatuses);
        }
        onError?.();
      },
      onSuccess: (__0, t3) => {
        const {
          data: data_1,
          onSuccess
        } = t3;
        queryClient.invalidateQueries({
          queryKey: getRecommendationsRulesStatusesQueryKey(data_1.clientId)
        });
        onSuccess?.();
      },
      onSettled: (__1, __, t4) => {
        const {
          data: data_2,
          onSettled
        } = t4;
        queryClient.invalidateQueries({
          queryKey: getRecommendationsRulesStatusesQueryKey(data_2.clientId)
        });
        onSettled?.();
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
interface MutationUpdateRecommendationsRulesStatusesParams extends MutationBaseParams {
  data: RecommendationRuleStatusRequest;
  statusId: number;
  clientId: number;
}
export const useMutationUpdateRecommendationsRulesStatuses = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp12,
      onMutate: async t1 => {
        const {
          data: data_0,
          statusId: statusId_0,
          clientId: clientId_0
        } = t1;
        const statusesQueryKey = getRecommendationsRulesStatusesQueryKey(clientId_0);
        await queryClient.cancelQueries({
          queryKey: statusesQueryKey
        });
        const previousStatuses = queryClient.getQueryData(statusesQueryKey);
        const singleStatusKey = [queryKeys.recommendationsRulesStatuses, statusId_0, clientId_0];
        const previousSingleStatus = queryClient.getQueryData(singleStatusKey);
        if (previousStatuses != null) {
          queryClient.setQueryData(statusesQueryKey, old => {
            if (old == null) {
              return [];
            }
            if (data_0 == null) {
              return old.filter(t2 => {
                const {
                  id
                } = t2;
                return id !== statusId_0;
              });
            }
            return old.map(status => {
              if (status.id === statusId_0) {
                return {
                  id: status.id,
                  createdBy: status.createdBy,
                  createdAt: status.createdAt,
                  updatedBy: status.updatedBy,
                  updatedAt: status.updatedAt,
                  status: status.status,
                  ...data_0
                };
              }
              return status;
            });
          });
        }
        if (previousSingleStatus != null) {
          if (data_0 == null) {
            queryClient.removeQueries({
              queryKey: singleStatusKey
            });
          } else {
            queryClient.setQueryData(singleStatusKey, {
              id: previousSingleStatus.id,
              createdBy: previousSingleStatus.createdBy,
              createdAt: previousSingleStatus.createdAt,
              updatedBy: previousSingleStatus.updatedBy,
              updatedAt: previousSingleStatus.updatedAt,
              status: previousSingleStatus.status,
              ...data_0
            });
          }
        }
        return {
          previousStatuses,
          previousSingleStatus,
          singleStatusKey,
          statusesQueryKey
        };
      },
      onError: (_, t3, context) => {
        const {
          onError
        } = t3;
        if (context?.previousStatuses != null && context.statusesQueryKey != null) {
          queryClient.setQueryData(context.statusesQueryKey, context.previousStatuses);
        }
        if (context?.previousSingleStatus != null) {
          queryClient.setQueryData(context.singleStatusKey, context.previousSingleStatus);
        }
        onError?.();
      },
      onSuccess: (__0, t4, context_0) => {
        const {
          onSuccess,
          clientId: clientId_1
        } = t4;
        queryClient.invalidateQueries({
          queryKey: getRecommendationsRulesStatusesQueryKey(clientId_1)
        });
        if (context_0?.singleStatusKey != null) {
          queryClient.invalidateQueries({
            queryKey: context_0.singleStatusKey
          });
        }
        onSuccess?.();
      },
      onSettled: (__1, __, t5) => {
        const {
          onSettled,
          clientId: clientId_2
        } = t5;
        queryClient.invalidateQueries({
          queryKey: getRecommendationsRulesStatusesQueryKey(clientId_2)
        });
        onSettled?.();
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
interface MutationDeleteRecommendationsRulesStatusesParams extends MutationBaseParams {
  statusId: number;
  clientId: number;
}
export const useMutationDeleteRecommendationsRulesStatuses = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp13,
      onMutate: async t1 => {
        const {
          statusId: statusId_0,
          clientId: clientId_0
        } = t1;
        const statusesQueryKey = getRecommendationsRulesStatusesQueryKey(clientId_0);
        await queryClient.cancelQueries({
          queryKey: statusesQueryKey
        });
        const previousStatuses = queryClient.getQueryData(statusesQueryKey);
        const singleStatusKey = [queryKeys.recommendationsRulesStatuses, statusId_0, clientId_0];
        const previousSingleStatus = queryClient.getQueryData(singleStatusKey);
        if (previousStatuses != null) {
          queryClient.setQueryData(statusesQueryKey, old => {
            if (old == null) {
              return [];
            }
            return old.filter(t2 => {
              const {
                id
              } = t2;
              return id !== statusId_0;
            });
          });
        }
        if (previousSingleStatus != null) {
          queryClient.removeQueries({
            queryKey: singleStatusKey
          });
        }
        return {
          previousStatuses,
          previousSingleStatus,
          singleStatusKey,
          statusesQueryKey
        };
      },
      onError: (_, t3, context) => {
        const {
          onError
        } = t3;
        if (context?.previousStatuses != null && context.statusesQueryKey != null) {
          queryClient.setQueryData(context.statusesQueryKey, context.previousStatuses);
        }
        if (context?.previousSingleStatus != null) {
          queryClient.setQueryData(context.singleStatusKey, context.previousSingleStatus);
        }
        onError?.();
      },
      onSuccess: (__0, t4, context_0) => {
        const {
          onSuccess
        } = t4;
        if (context_0?.singleStatusKey != null) {
          queryClient.invalidateQueries({
            queryKey: context_0.singleStatusKey
          });
        }
        onSuccess?.();
      },
      onSettled: (__1, __, t5) => {
        const {
          onSettled,
          clientId: clientId_1
        } = t5;
        queryClient.invalidateQueries({
          queryKey: getRecommendationsRulesStatusesQueryKey(clientId_1)
        });
        onSettled?.();
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
async function _temp() {
  return await getRecommendationsRulesMetadataJson();
}
function _temp2(data) {
  return createRecommendationRuleMetadataFromJson(data);
}
async function _temp3() {
  return await getRecommendationsRulesMetadataJson();
}
function _temp4(t0) {
  const [, metadataJson] = t0;
  return !isOnExcludedPlatform(metadataJson);
}
function _temp5(t0) {
  const [, metadataJson_0] = t0;
  return getPlatformFromMetadataJson(metadataJson_0);
}
function _temp6(data) {
  return uniq(objectEntries(data).filter(_temp4).map(_temp5));
}
async function _temp7(t0) {
  const {
    data
  } = t0;
  return await createRecommendationRules(data);
}
function _temp8(__1, __, t0) {
  const {
    onSettled
  } = t0;
  onSettled?.();
}
async function _temp9(t0) {
  const {
    data,
    configId,
    clientId
  } = t0;
  return await updateRecommendationRules({
    data,
    configId,
    clientId
  });
}
async function _temp10(t0) {
  const {
    configId,
    clientId
  } = t0;
  return await deleteRecommendationRules({
    configId,
    clientId
  });
}
async function _temp11(t0) {
  const {
    data
  } = t0;
  return await createRecommendationsRulesStatuses(data);
}
async function _temp12(t0) {
  const {
    data,
    statusId,
    clientId
  } = t0;
  return await updateRecommendationsRulesStatuses({
    data,
    statusId,
    clientId
  });
}
async function _temp13(t0) {
  const {
    statusId,
    clientId
  } = t0;
  return await deleteRecommendationsRulesStatuses({
    statusId,
    clientId
  });
}